/**
 * フレンド招待（T16）サービス層。
 *
 * Hub の Friends API を使ってフレンド招待リンクの生成・受諾・共有を行う。
 *
 * 主要 API:
 *   - POST /games/:gameId/friends/invites   — 招待コード生成
 *   - POST /games/:gameId/friends/accept     — 招待受諾
 *
 * SDK メソッド → ymgFetch フォールバックパターン（他サービスと同様）。
 * エラーはサイレントに握り潰さず、呼び出し側にそのまま伝播させる
 *  (`.claude/rules/no-silent-fallback.md`)。
 *
 * LINE Mini App:
 *   LINE MINI App で友だちに URL を送る場合は Firebase Hosting の生 URL ではなく、
 *   `https://miniapp.line.me/{liffId}` 形式の permanent link を共有する。
 */

import { getYmgSdk, getGameId, ymgFetch, hasCapacitorSessionToken } from './sdk'
import { getAuthState } from './auth'
import { getPlatformId } from '../utils/platform'
import {
  createLineMiniAppPermanentLink,
  createInviteFlexMessage,
  shareWithFlexMessage,
  type ShareMessage,
} from './liff'
import {
  INVITE_ACCEPT_BACKOFF_BASE_MS,
  INVITE_ACCEPT_MAX_RETRY,
  INVITE_CONSUMED_MAX_ENTRIES,
  INVITE_CONSUMED_STORAGE_KEY,
  INVITE_PARAM_KEY,
  INVITE_SHARE_TEXT_TEMPLATE,
  INVITE_SHARE_TITLE,
  INVITE_STORAGE_KEY,
  type InviteCreateResponse,
  type InviteShareResult,
} from '../config/invite'
import { decodeLiffState, parseHashQuery, parseLiffStateQuery } from '../utils/liffState'

// ---------------------------------------------------------------------------
// 招待コード生成
// ---------------------------------------------------------------------------

/**
 * 招待コードと招待 URL を生成する。
 *
 * 現在のページ URL をベースに `?ymg_invite=<code>` を付与した URL を返す。
 * 既存のクエリパラメータ（liff.state 等）は保持する。
 *
 * 未認証時は Hub の API が 401 を返すため、上流にそのまま throw させる。
 * 呼び出し側（InviteFriendsModal）でログインモーダルへの導線を示す。
 */
export async function generateFriendInvite(): Promise<InviteCreateResponse> {
  const gameId = getGameId()
  const preferFetch = await shouldUseAuthenticatedFetch()

  let code: string | undefined
  let expiresAt: number | undefined

  // signed_in: ymgFetch 一本（重複招待コード発行を防止）
  if (preferFetch) {
    const res = await ymgFetch<{
      ok: boolean
      code: string
      expiresAt?: number
    }>(`/games/${encodeURIComponent(gameId)}/friends/invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    if (!res?.code) {
      throw new Error('[social] createFriendInvite returned no code')
    }
    return { code: res.code, url: buildInviteUrl(res.code), expiresAt: res.expiresAt }
  }

  // ゲスト: SDK メソッドがあれば優先、なければ ymgFetch
  const sdk = await getYmgSdk()
  if (typeof sdk.api.createFriendInvite === 'function') {
    const res = await sdk.api.createFriendInvite({ gameId })
    if (res?.code) {
      code = res.code
      expiresAt = res.expiresAt
    }
  }

  if (!code) {
    const res = await ymgFetch<{
      ok: boolean
      code: string
      expiresAt?: number
    }>(`/games/${encodeURIComponent(gameId)}/friends/invites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    if (!res?.code) {
      throw new Error('[social] createFriendInvite returned no code')
    }
    code = res.code
    expiresAt = res.expiresAt
  }

  const url = buildInviteUrl(code)
  return { code, url, expiresAt }
}

/**
 * 現在のページ URL を基に招待 URL を構築する。
 * 既存のクエリパラメータは保持しつつ `ymg_invite` を上書きする。
 * ハッシュ部分は招待に使わないので落とす（LIFF の `liff.state` とぶつからない）。
 */
function buildInviteUrl(code: string): string {
  if (typeof window === 'undefined') {
    // SSR / ユニットテスト等では window が無い。招待は UI 経由なので
    // ここには来ない想定だが、throw して明示的に気付けるようにする。
    throw new Error('[social] buildInviteUrl called outside browser context')
  }
  const url = new URL(window.location.href)
  url.searchParams.set(INVITE_PARAM_KEY, code)
  // ハッシュは保持しない（招待リンクとしては不要）
  url.hash = ''
  return createLineMiniAppPermanentLink(url.toString()) ?? url.toString()
}

// ---------------------------------------------------------------------------
// URL からの招待コード抽出
// ---------------------------------------------------------------------------

/**
 * 現在の URL から招待コードを抽出する。
 *
 * 探索順:
 *   1. `window.location.search` の `ymg_invite`
 *   2. `window.location.hash` をクエリ文字列として解釈した中の `ymg_invite`
 *   3. `liff.state` パラメータをデコードした中身（LIFF 未初期化でも動く）
 *
 * LIFF SDK が未初期化でも動くように、`window.liff` の有無は参照しない。
 * 文字列操作のみで解析する。
 */
export function captureInviteFromUrl(): string | null {
  if (typeof window === 'undefined') return null

  try {
    // 1. 通常のクエリパラメータ
    const search = new URLSearchParams(window.location.search)
    const direct = normalizeInviteCode(search.get(INVITE_PARAM_KEY))
    if (direct) return direct

    // 2. ハッシュ部分もクエリ文字列として解釈（hash router 対応）
    const inHash = normalizeInviteCode(parseHashQuery(window.location.hash).get(INVITE_PARAM_KEY))
    if (inHash) return inHash

    // 3. liff.state（LIFF リダイレクト経由）
    const liffState = search.get('liff.state')
    if (liffState) {
      try {
        const stateParams = parseLiffStateQuery(liffState)
        const inState = normalizeInviteCode(stateParams.get(INVITE_PARAM_KEY))
        if (inState) return inState
      } catch (err) {
        console.warn('[social] Failed to decode liff.state:', err)
      }
    }
  } catch (err) {
    // fallback:ok — 招待コード抽出は URL 解析のみ。例外時は「招待がない」扱いに
    // すれば良く、ゲーム進行を妨げない。詳細は warn ログに残す。
    console.warn('[social] captureInviteFromUrl failed:', err)
  }
  return null
}

/** 招待コード文字列を正規化（trim / 空判定 / 明らかな失敗マーカーの除外）。 */
function normalizeInviteCode(raw: string | null): string | null {
  if (!raw) return null
  const value = raw.trim()
  if (!value) return null
  // 一部のプラットフォームで "null" や "undefined" という文字列が流れ込むことがある。
  const lower = value.toLowerCase()
  if (
    lower === 'null' ||
    lower === 'undefined' ||
    lower === 'none' ||
    lower === 'failed'
  ) {
    return null
  }
  if (value.length > 256) return null
  if (/\s/.test(value)) return null
  return value
}

function removeInviteFromLiffState(raw: string): string | null {
  const decoded = decodeLiffState(raw)
  const queryStart = decoded.indexOf('?')
  const prefix = queryStart >= 0 ? decoded.slice(0, queryStart) : ''
  const query = queryStart >= 0
    ? decoded.slice(queryStart + 1)
    : decoded.startsWith('?')
      ? decoded.slice(1)
      : decoded
  const params = new URLSearchParams(query)
  if (!params.has(INVITE_PARAM_KEY)) return raw

  params.delete(INVITE_PARAM_KEY)
  const nextQuery = params.toString()
  if (!nextQuery) return prefix || null
  return prefix ? `${prefix}?${nextQuery}` : nextQuery
}

/**
 * URL から招待パラメータを取り除く（履歴を汚さない）。
 * 他のクエリパラメータ（liff.state 等）は保持する。
 */
export function clearInviteFromUrl(): void {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    let changed = false
    if (url.searchParams.has(INVITE_PARAM_KEY)) {
      url.searchParams.delete(INVITE_PARAM_KEY)
      changed = true
    }
    // liff.state 内の招待パラメータも選択的に除去
    const liffState = url.searchParams.get('liff.state')
    if (liffState) {
      try {
        const next = removeInviteFromLiffState(liffState)
        if (next !== liffState) {
          if (next) url.searchParams.set('liff.state', next)
          else url.searchParams.delete('liff.state')
          changed = true
        }
      } catch {
        // liff.state が想定外フォーマットなら触らない
      }
    }
    if (changed) {
      window.history.replaceState(
        {},
        document.title,
        url.pathname + url.search + url.hash,
      )
    }
  } catch (err) {
    console.warn('[social] clearInviteFromUrl failed:', err)
  }
}

// ---------------------------------------------------------------------------
// 招待受諾
// ---------------------------------------------------------------------------

/**
 * 招待コードを受諾してフレンドになる。
 *
 * 429 / 5xx / ネットワークエラーは指数バックオフで最大 INVITE_ACCEPT_MAX_RETRY 回
 * までリトライする。それ以外のエラー（400 / 401 / 403 / 404 / 410）は即 throw。
 *
 * 呼び出し側は UI にエラー表示する責務を持つ（`.claude/rules/no-silent-fallback.md`）。
 */
export async function acceptFriendInvite(
  code: string,
): Promise<YmgFriendInviteAcceptResponse> {
  const trimmed = code.trim()
  if (!trimmed) throw new Error('[social] invite code is empty')

  const gameId = getGameId()
  const preferFetch = await shouldUseAuthenticatedFetch()
  const sdk = preferFetch ? null : await getYmgSdk()

  let lastErr: unknown = null
  for (let attempt = 0; attempt < INVITE_ACCEPT_MAX_RETRY; attempt++) {
    try {
      // 1st: SDK メソッド
      if (preferFetch) {
        return await ymgFetch<YmgFriendInviteAcceptResponse>(
          `/games/${encodeURIComponent(gameId)}/friends/accept`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: trimmed }),
          },
        )
      }
      if (sdk && typeof sdk.api.acceptFriendInvite === 'function') {
        return await sdk.api.acceptFriendInvite({ gameId, code: trimmed })
      }
      // 2nd: fetch フォールバック
      const res = await ymgFetch<YmgFriendInviteAcceptResponse>(
        `/games/${encodeURIComponent(gameId)}/friends/accept`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: trimmed }),
        },
      )
      return res
    } catch (err) {
      lastErr = err
      if (!isRetryableError(err) || attempt === INVITE_ACCEPT_MAX_RETRY - 1) {
        throw err
      }
      // 指数バックオフ（+/-20% のジッター）
      const base = INVITE_ACCEPT_BACKOFF_BASE_MS * Math.pow(2, attempt)
      const jitter = base * (Math.random() * 0.4 - 0.2)
      await sleep(base + jitter)
    }
  }
  // 通常ここには来ないが、型のため throw
  throw lastErr ?? new Error('[social] acceptFriendInvite failed')
}

async function shouldUseAuthenticatedFetch(): Promise<boolean> {
  if (getAuthState().status === 'signed_in') return true
  if (getPlatformId() === 'line_mini') return true
  return hasCapacitorSessionToken()
}

function isRetryableError(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status
  if (status === 429) return true
  if (status !== undefined && status >= 500 && status < 600) return true
  // status が無い = ネットワークエラー or TypeError
  if (status === undefined) return true
  return false
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

// ---------------------------------------------------------------------------
// pending 招待の処理（起動時）
// ---------------------------------------------------------------------------

/**
 * 起動時に呼び出す招待処理エントリ。
 *
 * フロー:
 *   1. 現在の URL から招待コードを取得（`captureInviteFromUrl`）
 *      無ければ localStorage の pending を確認
 *   2. 重複受諾防止のため、消費済みキャッシュに含まれていたら URL をクリーン
 *      して終了（成功扱い）
 *   3. 未認証の場合は localStorage に pending として保存し、false を返す
 *      （呼び出し側は認証後にこの関数を再実行する）
 *   4. `acceptFriendInvite()` を呼ぶ
 *   5. 成功したら URL をクリーン + 消費済みキャッシュに記録 + pending をクリア
 *
 * @returns 成功時 true / コードなし or pending 保存のみは false
 * @throws 受諾の呼び出し自体が失敗した場合（ネットワーク / サーバーエラー等）
 */
export async function processPendingInvite(): Promise<boolean> {
  // 1. URL から取得
  let code = captureInviteFromUrl()

  // 1.5. 無ければ pending を確認
  if (!code) {
    code = readPendingInvite()
  }

  if (!code) return false

  // 2. 消費済みチェック
  if (isInviteConsumed(code)) {
    clearInviteFromUrl()
    clearPendingInvite()
    return false
  }

  // 3. 認証状態の確認
  if (!(await shouldUseAuthenticatedFetch())) {
    // 未認証なら pending として保存して後で再試行
    savePendingInvite(code)
    return false
  }

  // 4. 受諾
  try {
    await acceptFriendInvite(code)
  } catch (err) {
    // 404 / 410 (invite not found / expired) は消費済みとして扱い、
    // 以後このコードを処理しないようにする（無限リトライ防止）。
    const status = (err as { status?: number } | null)?.status
    if (status === 404 || status === 410 || status === 403) {
      markInviteConsumed(code)
      clearInviteFromUrl()
      clearPendingInvite()
    }
    throw err
  }

  // 5. 成功処理
  markInviteConsumed(code)
  clearInviteFromUrl()
  clearPendingInvite()
  return true
}

// ---------------------------------------------------------------------------
// localStorage: pending invite
// ---------------------------------------------------------------------------

function readPendingInvite(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(INVITE_STORAGE_KEY)
    return normalizeInviteCode(raw)
  } catch (err) {
    console.warn('[social] readPendingInvite failed:', err)
    return null
  }
}

function savePendingInvite(code: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(INVITE_STORAGE_KEY, code)
  } catch (err) {
    console.warn('[social] savePendingInvite failed:', err)
  }
}

function clearPendingInvite(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(INVITE_STORAGE_KEY)
  } catch (err) {
    console.warn('[social] clearPendingInvite failed:', err)
  }
}

// ---------------------------------------------------------------------------
// localStorage: consumed codes
// ---------------------------------------------------------------------------

function readConsumed(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(INVITE_CONSUMED_STORAGE_KEY)
    if (!raw) return []
    const arr = JSON.parse(raw) as unknown
    if (!Array.isArray(arr)) return []
    return arr.filter((v): v is string => typeof v === 'string')
  } catch (err) {
    console.warn('[social] readConsumed failed:', err)
    return []
  }
}

function isInviteConsumed(code: string): boolean {
  return readConsumed().includes(code)
}

function markInviteConsumed(code: string): void {
  if (typeof window === 'undefined') return
  try {
    const list = readConsumed().filter((c) => c !== code)
    list.push(code)
    // FIFO で溢れたものを捨てる
    const trimmed = list.slice(-INVITE_CONSUMED_MAX_ENTRIES)
    window.localStorage.setItem(
      INVITE_CONSUMED_STORAGE_KEY,
      JSON.stringify(trimmed),
    )
  } catch (err) {
    console.warn('[social] markInviteConsumed failed:', err)
  }
}

// ---------------------------------------------------------------------------
// 共有
// ---------------------------------------------------------------------------

/**
 * 招待 URL を共有する。
 *
 * 共有方法の優先順位:
 *   1. `platformId === 'line_mini'` かつ `window.liff?.shareTargetPicker` 有効
 *      → LIFF shareTargetPicker（text メッセージを送信）
 *   2. `navigator.share` が利用可能 → Web Share API
 *   3. Capacitor Share Plugin（`@capacitor/share`）が利用可能 → それ経由
 *   4. それ以外 → `navigator.clipboard.writeText` で URL をコピー
 *
 * ユーザーがネイティブ共有シートをキャンセルしたとき（AbortError）は、
 * `success: false` / `method: 'web_share'` を返し、UI 側でキャンセルメッセージを
 * 出せるようにする。
 */
export async function shareInvite(
  url: string,
  text: string = INVITE_SHARE_TEXT_TEMPLATE.replace('{url}', url),
): Promise<InviteShareResult> {
  // --- 1. LIFF (line_mini) ---
  const platformId = getPlatformId()
  if (platformId === 'line_mini') {
    const liffResult = await tryLiffShare(url, text)
    if (liffResult) return liffResult
  }

  // --- 2. Web Share API ---
  const webShareResult = await tryWebShare(url, text)
  if (webShareResult) return webShareResult

  // --- 3. Capacitor Share Plugin ---
  const capacitorResult = await tryCapacitorShare(url, text)
  if (capacitorResult) return capacitorResult

  // --- 4. Clipboard フォールバック ---
  return await fallbackToClipboard(url)
}

/**
 * LIFF shareTargetPicker 経由で招待を共有する。
 *
 * 優先順位:
 *   1. Flex Message（バナー画像 + ゲーム説明 + 遊んでみるボタン + 審査用フッター）
 *   2. プレーンテキスト（Flex 非対応/拒否時のフォールバック）
 *
 * 戻り値が null のときは呼び出し側 (shareInvite) が次の経路（Web Share → Capacitor → clipboard）
 * に進む。null を返すのは「LIFF 自体が使えない」ケースのみ。
 */
async function tryLiffShare(
  url: string,
  text: string,
): Promise<InviteShareResult | null> {
  const w = window as unknown as {
    liff?: {
      shareTargetPicker?: (
        messages: ShareMessage[],
      ) => Promise<{ status?: string } | void>
      isApiAvailable?: (name: string) => boolean
      getLanguage?: () => string
    }
  }
  const liff = w.liff
  if (!liff?.shareTargetPicker) return null
  // isApiAvailable があれば先にチェック
  try {
    if (
      typeof liff.isApiAvailable === 'function' &&
      !liff.isApiAvailable('shareTargetPicker')
    ) {
      return null
    }
  } catch {
    // fallback:ok — isApiAvailable が古い LIFF SDK で未実装/バグのケースを許容。
    // shareTargetPicker 本体が存在するので続行して試す。
  }

  // 1. Flex Message を試す
  try {
    const locale =
      (typeof liff.getLanguage === 'function' ? liff.getLanguage() : null) || 'ja_JP'
    const flex = createInviteFlexMessage(url, locale)
    const ok = await shareWithFlexMessage(flex)
    if (ok) return { success: true, method: 'liff' }
    // Flex 不成立 → text フォールバックへ進む
  } catch (err) {
    // fallback:ok — Flex 生成/送信例外時は text フォールバックに委譲する。
    // ここで throw するとユーザーに共有手段が無くなるため、次段へ進める設計。
    console.warn('[social] Flex share failed, falling back to text:', err)
  }

  // 2. プレーンテキストフォールバック
  try {
    const res = await liff.shareTargetPicker([{ type: 'text', text } as ShareMessage])
    const status = res && typeof res === 'object' ? res.status : undefined
    if (status === 'success' || status === undefined) {
      // status 未返却の古い LIFF もあるため成功扱い
      return { success: true, method: 'liff' }
    }
    return { success: false, method: 'liff', error: `status=${status}` }
  } catch (err) {
    console.warn('[social] liff.shareTargetPicker (text fallback) failed:', err)
    return {
      success: false,
      method: 'liff',
      error: err instanceof Error ? err.message : String(err),
    }
  }
}

async function tryWebShare(
  url: string,
  text: string,
): Promise<InviteShareResult | null> {
  if (typeof navigator === 'undefined') return null
  const nav = navigator as Navigator & {
    share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>
    canShare?: (data: { title?: string; text?: string; url?: string }) => boolean
  }
  if (typeof nav.share !== 'function') return null

  const data = {
    title: INVITE_SHARE_TITLE,
    text,
    url,
  }
  try {
    if (typeof nav.canShare === 'function' && !nav.canShare(data)) {
      return null
    }
    await nav.share(data)
    return { success: true, method: 'web_share' }
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') {
      // ユーザーがキャンセル
      return { success: false, method: 'web_share', error: 'cancelled' }
    }
    console.warn('[social] navigator.share failed:', err)
    // 次の fallback に進むため null を返す
    return null
  }
}

async function tryCapacitorShare(
  url: string,
  text: string,
): Promise<InviteShareResult | null> {
  // @capacitor/share は現時点で package.json に無いので、
  // 動的 import 文字列を変数経由にして TS の静的解決を回避する。
  // 将来 `npm i @capacitor/share` した時点で自動で有効化される分岐枠。
  try {
    const moduleName = '@capacitor/share'
    const mod = (await import(/* @vite-ignore */ moduleName).catch(() => null)) as
      | { Share?: { share?: (opts: unknown) => Promise<unknown> } }
      | null
    if (!mod?.Share?.share) return null
    try {
      await mod.Share.share({
        title: INVITE_SHARE_TITLE,
        text,
        url,
        dialogTitle: INVITE_SHARE_TITLE,
      })
      return { success: true, method: 'capacitor_share' }
    } catch (err) {
      // Capacitor Share は「ユーザーがキャンセル」を throw するが Error の .message に
      // 'Share canceled' が入るのでそれで判別する。詳細は plugin 実装次第。
      const msg = err instanceof Error ? err.message : String(err)
      if (msg.toLowerCase().includes('cancel')) {
        return { success: false, method: 'capacitor_share', error: 'cancelled' }
      }
      console.warn('[social] capacitor Share failed:', err)
      return null
    }
  } catch {
    // import 自体が失敗した場合は plugin 無し扱い
    return null
  }
}

async function fallbackToClipboard(url: string): Promise<InviteShareResult> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    return {
      success: false,
      method: 'clipboard',
      error: 'clipboard API not available',
    }
  }
  try {
    await navigator.clipboard.writeText(url)
    return { success: true, method: 'clipboard' }
  } catch (err) {
    console.warn('[social] clipboard writeText failed:', err)
    return {
      success: false,
      method: 'clipboard',
      error: err instanceof Error ? err.message : String(err),
    }
  }
}
